import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import { OrderDetail } from './components/OrderDetail'
import { OrderBox } from './components/OrderBox'
import { useEffect, useMemo, useState } from 'react'
import type { BillType } from '../../all-types'
import { Chip, IconButton, Tab, Tabs, Modal, useTheme } from '@mui/material'
import { startAndEndTime } from '../../utils'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { DatePickerComponent } from './components/DatePickerComponent'
import { getBills } from '../../services'

const getBillId = (b: any): string =>
  String(b?._id ?? b?.id ?? b?.billNo ?? `${b?.billNo ?? ''}-${b?.createdAt ?? b?.date ?? ''}`)

const cloneBill = (bill: BillType): BillType => JSON.parse(JSON.stringify(bill))

const BillingHistory = () => {
  const today = new Date()
  const { startTimeStamp, endTimeStamp } = startAndEndTime(today, today)

  const theme = useTheme()
  const [rawData, setRawData] = useState<BillType[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const [selectedBill, setSelectedBill] = useState<BillType | null>(null)
  const [value, setValue] = useState<number>(1)

  const [startTime, setStartTime] = useState<number>(startTimeStamp)
  const [endTime, setEndTime] = useState<number>(endTimeStamp)

  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)

  const handleChange = (_: React.SyntheticEvent, change: number) => {
    setValue(change)
    handleDay(change)
  }

  const updateSelectedBill = async (b: BillType) => {
    try {
      setSelectedBill(cloneBill(b))
      await load();
    } catch(e) {
      console.log(e)
    }
  }

  const handleDay = (val: number) => {
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    if (val === 1) {
      setStartTime(startTimeStamp)
      setEndTime(endTimeStamp)
    } else if (val === 0) {
      const { startTimeStamp: s, endTimeStamp: e } = startAndEndTime(yesterday, yesterday)
      setStartTime(s)
      setEndTime(e)
    }
  }

  const load = async () => {
    try {
      const resp = await getBills(startTime, endTime)
      const result: BillType[] = Array.isArray(resp?.bills) ? resp.bills : [];
      setRawData(result);
    } catch(e) {
      console.error(e)
    }
  }

  // Fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true)
        setError('')
        const resp = await getBills(startTime, endTime)
        const result: BillType[] = Array.isArray(resp?.bills)
          ? resp.bills.map((bill: BillType) => cloneBill(bill))
          : []
        if (!cancelled) setRawData(result)
      } catch (e) {
        console.error(e)
        if (!cancelled) setError('Oops! Something went wrong. Please try again')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [startTime, endTime])

  // De-dupe + (optional) sort newest first
  const data = useMemo<BillType[]>(() => {
    const map = new Map<string, BillType>()
    for (const b of rawData) {
      const id = getBillId(b)
      if (!map.has(id)) map.set(id, cloneBill(b))
    }
    const arr = Array.from(map.values())
    arr.sort((a: any, b: any) => {
      const at = new Date(a?.createdAt ?? a?.timeInfo?.timestamp ?? 0).getTime()
      const bt = new Date(b?.createdAt ?? b?.timeInfo?.timestamp ?? 0).getTime()
      if (at && bt && at !== bt) return bt - at
      const an = Number(a?.billNo); const bn = Number(b?.billNo)
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an
      return 0
    })
    return arr
  }, [rawData])

  // Keep selection stable by the same unique ID used as key
  useEffect(() => {
    if (!data.length) { setSelectedBill(null); return }
    const curId = selectedBill ? getBillId(selectedBill) : null
    const next = curId ? data.find(b => getBillId(b) === curId) : data[0]
    setSelectedBill(next ? cloneBill(next) : data[0] ? cloneBill(data[0]) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const periodLabel = (() => {
    const fmt = (ts: number) => new Date(ts).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' })
    return `${fmt(startTime)} — ${fmt(endTime)}`
  })()

  if (error) {
    return (
      <Container sx={{ width: '100dvw', height: '100dvh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ color: theme.palette.offline.main, fontSize: 24, fontWeight: 500 }}>{error}</Box>
      </Container>
    )
  }

  return (
    <Container sx={{ m: 0 }}>
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            {/* Tabs */}
            <Tabs value={value} onChange={handleChange} indicatorColor="primary" sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab value={0} label="Yesterday" sx={{ textTransform: 'capitalize' }} />
              <Tab value={1} label="Today" sx={{ textTransform: 'capitalize' }} />
            </Tabs>

            {/* Date Range Picker Icon */}
            <IconButton onClick={() => setOpenDatePicker(true)} disableRipple>
              <CalendarMonthIcon fontSize="large" sx={{ color: theme.palette.offline.main }} />
            </IconButton>

            {/* Enhancement: show selected From–To (tiny, non-invasive) */}
            <Chip size="small" variant="outlined" label={periodLabel} />
          </Box>

          {/* Order List */}
          <OrderBox
            data={data}
            loading={loading}
            selectedBillId={selectedBill ? getBillId(selectedBill) : undefined}
            setSelectedBill={setSelectedBill}
          />
        </Box>

        {/* Order Detail */}
        <OrderDetail
          key={selectedBill ? getBillId(selectedBill) : 'no-bill'}
          selectedBill={selectedBill}
          setSelectedBill={updateSelectedBill}
          refresh={load}
        />
      </Box>

      {/* Date Picker Modal */}
      <Modal open={openDatePicker} onClose={() => setOpenDatePicker(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, minWidth: 300, outline: 'none' }}>
          <DatePickerComponent
            selectRange={(start, end) => { setStartTime(start); setEndTime(end); setOpenDatePicker(false) }}
            onClose={() => setOpenDatePicker(false)}
          />
        </Box>
      </Modal>
    </Container>
  )
}

export { BillingHistory }
